import { useState, useEffect } from "react";
import api from "../../utils/api";
import { uploadImageFile } from "../../utils/imageUtils";
import { INP, LBL, S, THEME, ImageUploadField, useIsMobile } from "./AdminComponents";
import { useLanguage } from "../../context/LanguageContext";

const { G, G3, O2 } = THEME;

const CATEGORY_LABELS = {
  ne: {
    report: "प्रतिवेदन",
    logo:   "लोगो / ब्रान्ड",
    news:   "समाचार कागजात",
    form:   "फारमहरू",
    policy: "नीति",
  },
  en: {
    report: "Report",
    logo:   "Logo / Brand",
    news:   "News Document",
    form:   "Forms",
    policy: "Policy",
  },
};

const CATEGORY_IDS = ["report", "logo", "news", "form", "policy"];

const CONTENT = {
  ne: {
    statTotal:         "कुल फाइल",
    statReport:        "प्रतिवेदन",
    statForm:          "फारम",
    statPolicy:        "नीति",
    deltaSoFar:        "अहिलेसम्म",
    deltaReport:       "रिपोर्ट",
    deltaForm:         "फारम",
    deltaPolicy:       "नीति",
    searchPlaceholder: "🔍 डाउनलोड खोज्नुहोस्…",
    filterAll:         "सबै वर्ग",
    filterAllSector:   "सबै क्षेत्र",
    refresh:           "↻ ताजा गर्नुहोस्",
    addBtn:            "+ डाउनलोड थप्नुहोस्",
    colTitle:          "शीर्षक",
    colCategory:       "वर्ग",
    colSector:         "क्षेत्र",
    colType:           "प्रकार",
    colSize:           "साइज",
    colDate:           "मिति",
    colActions:        "कार्य",
    loading:           "डाउनलोड लोड हुँदैछ…",
    empty:             "कुनै डाउनलोड भेटिएन।",
    edit:              "सम्पादन",
    delete:            "मेटाउनुहोस्",
    modalAdd:          "➕ डाउनलोड थप्नुहोस्",
    modalEdit:         "✏️ डाउनलोड सम्पादन",
    lblTitle:          "शीर्षक *",
    lblDescription:    "विवरण",
    lblCategory:       "वर्ग",
    lblSector:         "क्षेत्र",
    selectSector:      "क्षेत्र छान्नुहोस्",
    lblFileType:       "फाइल प्रकार",
    lblFileSize:       "फाइल साइज",
    lblFile:           "फाइल *",
    lblThumbnail:      "थम्बनेल छवि (वैकल्पिक)",
    uploaded:          "✓ अपलोड भयो:",
    saveBtn:           "💾 सुरक्षित गर्नुहोस्",
    savingBtn:         "सुरक्षित हुँदैछ…",
    cancel:            "रद्द गर्नुहोस्",
    deleteConfirm:     (title) => <>के तपाईं <strong>"{title}"</strong> मेटाउन निश्चित हुनुहुन्छ?</>,
    deleteYes:         "हो, मेटाउनुहोस्",
    errLoad:           "डाउनलोड लोड गर्न असफल भयो।",
    errSave:           "सुरक्षित गर्न असफल भयो।",
    errDelete:         "मेटाउन असफल भयो।",
    errUpload:         "फाइल अपलोड गर्न असफल भयो।",
    locale:            "ne-NP",
  },
  en: {
    statTotal:         "Total Files",
    statReport:        "Reports",
    statForm:          "Forms",
    statPolicy:        "Policies",
    deltaSoFar:        "So far",
    deltaReport:       "Reports",
    deltaForm:         "Forms",
    deltaPolicy:       "Policy",
    searchPlaceholder: "🔍 Search downloads…",
    filterAll:         "All Categories",
    filterAllSector:   "All Sectors",
    refresh:           "↻ Refresh",
    addBtn:            "+ Add Download",
    colTitle:          "Title",
    colCategory:       "Category",
    colSector:         "Sector",
    colType:           "Type",
    colSize:           "Size",
    colDate:           "Date",
    colActions:        "Actions",
    loading:           "Loading downloads…",
    empty:             "No downloads found.",
    edit:              "Edit",
    delete:            "Delete",
    modalAdd:          "➕ Add Download",
    modalEdit:         "✏️ Edit Download",
    lblTitle:          "Title *",
    lblDescription:    "Description",
    lblCategory:       "Category",
    lblSector:         "Sector",
    selectSector:      "Select a sector",
    lblFileType:       "File Type",
    lblFileSize:       "File Size",
    lblFile:           "File *",
    lblThumbnail:      "Thumbnail Image (optional)",
    uploaded:          "✓ Uploaded:",
    saveBtn:           "💾 Save",
    savingBtn:         "Saving…",
    cancel:            "Cancel",
    deleteConfirm:     (title) => <>Are you sure you want to delete <strong>"{title}"</strong>?</>,
    deleteYes:         "Yes, Delete",
    errLoad:           "Failed to load downloads.",
    errSave:           "Failed to save.",
    errDelete:         "Failed to delete.",
    errUpload:         "Failed to upload file.",
    locale:            "en-US",
  },
};

const EMPTY_DOWNLOAD_FORM = {
  title: "", description: "", category: "report", sector: "",
  fileType: "PDF", fileSize: "", fileUrl: "", thumbnailUrl: "",
};

function DownloadsAdminPage() {
  const { language } = useLanguage();
  const t   = CONTENT[language] || CONTENT.en;
  const cat = CATEGORY_LABELS[language] || CATEGORY_LABELS.en;
  const isMobile = useIsMobile();

  const DOWNLOAD_CATEGORIES = CATEGORY_IDS.map(id => ({ id, label: cat[id] }));

  const [items, setItems]         = useState([]);
  // Sectors are fetched dynamically from the Sectors API, so adding/editing
  // a sector on the Sectors admin page automatically reflects here too.
  const [sectors, setSectors]     = useState([]); // [{ _id, name, order, ... }]
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");
  const [search, setSearch]       = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [filterSector, setFilterSector] = useState("all");
  const [modal, setModal]         = useState(null);
  const [selected, setSelected]   = useState(null);
  const [form, setForm]           = useState({ ...EMPTY_DOWNLOAD_FORM });
  const [saving, setSaving]       = useState(false);

  useEffect(() => { fetchDownloads(); fetchSectors(); }, []);

  async function fetchDownloads() {
    setLoading(true); setError("");
    try {
      const res  = await api.get("/downloads");
      const data = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
      setItems(data);
    } catch (e) {
      setError(e.response?.data?.message || t.errLoad);
    } finally {
      setLoading(false);
    }
  }

  async function fetchSectors() {
    try {
      const res  = await api.get("/sectors");
      const data = Array.isArray(res.data) ? res.data : (res.data?.data ?? res.data?.sectors ?? []);
      const sorted = [...data].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setSectors(sorted);
    } catch (e) {
      console.error("Error fetching sectors:", e);
    }
  }

  const filtered = items.filter(d => {
    const q = search.toLowerCase();
    const matchSearch = d.title?.toLowerCase().includes(q) || d.description?.toLowerCase().includes(q);
    const matchCat = filterCat === "all" || d.category === filterCat;
    const matchSector = filterSector === "all" || d.sector === filterSector;
    return matchSearch && matchCat && matchSector;
  });

  const stats = [
    { label: t.statTotal,  value: items.length,                                          icon: "📥", delta: t.deltaSoFar  },
    { label: t.statReport, value: items.filter(d => d.category === "report").length,    icon: "📄", delta: t.deltaReport },
    { label: t.statForm,   value: items.filter(d => d.category === "form").length,      icon: "📋", delta: t.deltaForm   },
    { label: t.statPolicy, value: items.filter(d => d.category === "policy").length,    icon: "⚖", delta: t.deltaPolicy },
  ];

  function catLabel(id) { return cat[id] || id; }

  function openAdd()   { setForm({ ...EMPTY_DOWNLOAD_FORM, sector: sectors[0]?.name || "" }); setSelected(null); setModal("add"); }
  function openEdit(d) { setForm({ title: d.title, description: d.description || "", category: d.category, sector: d.sector || sectors[0]?.name || "", fileType: d.fileType || "PDF", fileSize: d.fileSize || "", fileUrl: d.fileUrl, thumbnailUrl: d.thumbnailUrl || "" }); setSelected(d); setModal("edit"); }
  function openDelete(d) { setSelected(d); setModal("delete"); }
  function closeModal() { setModal(null); setSelected(null); }

  async function handleSave() {
    if (!form.title.trim() || !form.fileUrl.trim()) return;
    setSaving(true);
    try {
      const thumbnailUrl = await uploadImageFile(form.thumbnailUrl);
      const payload      = { ...form, thumbnailUrl };
      if (modal === "add") {
        const res     = await api.post("/downloads", payload);
        const created = res.data?.data ?? res.data;
        setItems([created, ...items]);
      } else {
        const res     = await api.put(`/downloads/${selected._id}`, payload);
        const updated = res.data?.data ?? res.data;
        setItems(items.map(d => d._id === selected._id ? updated : d));
      }
      closeModal();
    } catch (e) {
      alert(e.response?.data?.message || t.errSave);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setSaving(true);
    try {
      await api.delete(`/downloads/${selected._id}`);
      setItems(items.filter(d => d._id !== selected._id));
      closeModal();
    } catch (e) {
      alert(e.response?.data?.message || t.errDelete);
    } finally {
      setSaving(false);
    }
  }

  function fmt(d) {
    if (!d) return "—";
    return new Date(d).toLocaleDateString(t.locale, { day: "numeric", month: "short", year: "numeric" });
  }

  const DL_COLS = "minmax(0,1.6fr) minmax(0,1fr) minmax(0,1.3fr) minmax(0,0.7fr) minmax(0,0.7fr) minmax(0,1fr) minmax(0,1.3fr)";

  const catBadge = {
    display: "inline-block", padding: "2px 10px", borderRadius: 20,
    fontSize: 10, fontWeight: 600, whiteSpace: "nowrap",
    background: O2, color: "#c2410c", border: "1px solid #c2410c",
    maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis",
    boxSizing: "border-box",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Stat cards: 4 on desktop, 2 on mobile */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 10 }}>
        {stats.map(s => (
          <div key={s.label} style={S.statCard}>
            <div style={S.statIcon}>{s.icon}</div>
            <div><div style={S.statVal}>{s.value}</div><div style={S.statLabel}>{s.label}</div><div style={S.statDelta}>{s.delta}</div></div>
          </div>
        ))}
      </div>

      <div style={{ ...S.card, padding: "12px 16px" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <input placeholder={t.searchPlaceholder} value={search} onChange={e => setSearch(e.target.value)} style={{ ...INP, flex: "1 1 200px", width: "auto", minWidth: 0, maxWidth: isMobile ? "100%" : 240 }} />
          <select value={filterSector} onChange={e => setFilterSector(e.target.value)} style={{ ...INP, width: "auto", minWidth: 150 }}>
            <option value="all">{t.filterAllSector}</option>
            {sectors.map(s => <option key={s._id} value={s.name}>{s.name}</option>)}
          </select>
          <select value={filterCat} onChange={e => setFilterCat(e.target.value)} style={{ ...INP, width: "auto", minWidth: 130 }}>
            <option value="all">{t.filterAll}</option>
            {DOWNLOAD_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          <button onClick={fetchDownloads} style={{ marginLeft: isMobile ? 0 : "auto", padding: "7px 14px", borderRadius: 7, background: G3, border: `1px solid ${G}`, color: G, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{t.refresh}</button>
          <button onClick={openAdd} style={{ padding: "7px 16px", borderRadius: 7, background: G, color: "#fff", border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{t.addBtn}</button>
        </div>
      </div>

      {error && <div style={{ background: "rgba(239,68,68,0.08)", border: "1px solid #fca5a5", borderRadius: 8, padding: "10px 16px", color: "#ef4444", fontSize: 13 }}>⚠ {error}</div>}

      <div style={{ ...S.tableWrap, ...S.tableScroll }}>
        <div style={{ minWidth: isMobile ? 820 : undefined }}>
          <div style={{ display: "grid", gridTemplateColumns: DL_COLS, gap: 14, padding: "8px 16px", background: "#f9fafb", borderBottom: "1px solid #e5e7eb", fontSize: 10, color: "#9ca3af", fontWeight: 700, letterSpacing: "0.06em" }}>
            <span>{t.colTitle}</span><span>{t.colSector}</span><span>{t.colCategory}</span><span>{t.colType}</span><span>{t.colSize}</span><span>{t.colDate}</span><span>{t.colActions}</span>
          </div>
          {loading && <div style={{ padding: 30, textAlign: "center", color: "#9ca3af", fontSize: 13 }}>{t.loading}</div>}
          {!loading && filtered.length === 0 && <div style={{ padding: 30, textAlign: "center", color: "#9ca3af", fontSize: 13 }}>{t.empty}</div>}
          {!loading && filtered.map((d, i) => (
            <div key={d._id} style={{ display: "grid", gridTemplateColumns: DL_COLS, gap: 14, padding: "10px 16px", alignItems: "center", borderBottom: i === filtered.length - 1 ? "none" : "1px solid #f3f4f6", fontSize: 12, color: "#374151" }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.title}</div>
                <div style={{ color: "#9ca3af", fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.description}</div>
              </div>
              <div style={{ minWidth: 0, overflow: "hidden" }} title={d.sector || ""}>
                {d.sector && <span style={catBadge}>{d.sector}</span>}
              </div>
              <span style={{ fontSize: 11, color: "#6b7280" }}>{catLabel(d.category)}</span>
              <span style={{ fontWeight: 600, color: G }}>{d.fileType}</span>
              <span style={{ color: "#6b7280" }}>{d.fileSize || "—"}</span>
              <span style={{ color: "#6b7280" }}>{fmt(d.createdAt)}</span>
              <span style={{ display: "flex", gap: 5 }}>
                <button onClick={() => openEdit(d)} style={{ background: G3, border: `1px solid ${G}`, color: G, borderRadius: 5, padding: "3px 8px", fontSize: 10, cursor: "pointer", fontWeight: 600 }}>{t.edit}</button>
                <button onClick={() => openDelete(d)} style={{ background: "rgba(239,68,68,0.1)", border: "1px solid #ef4444", color: "#ef4444", borderRadius: 5, padding: "3px 8px", fontSize: 10, cursor: "pointer", fontWeight: 600 }}>{t.delete}</button>
              </span>
            </div>
          ))}
        </div>
      </div>

      {(modal === "add" || modal === "edit") && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={closeModal}>
          <div style={{ background: "#fff", borderRadius: 14, width: 500, maxWidth: "95vw", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }} onClick={e => e.stopPropagation()}>
            <div style={{ background: `linear-gradient(135deg, ${G} 0%, #0a5c2e 100%)`, padding: "18px 22px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ color: "#fff", fontSize: 15, fontWeight: 700 }}>{modal === "add" ? t.modalAdd : t.modalEdit}</span>
              <button onClick={closeModal} style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", width: 26, height: 26, borderRadius: "50%", cursor: "pointer" }}>×</button>
            </div>
            <div style={{ padding: "18px 22px", display: "flex", flexDirection: "column", gap: 12 }}>
              <div><div style={LBL}>{t.lblTitle}</div><input style={INP} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></div>
              <div><div style={LBL}>{t.lblDescription}</div><textarea style={{ ...INP, minHeight: 70, resize: "vertical" }} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
                <div>
                  <div style={LBL}>{t.lblSector}</div>
                  <select style={INP} value={form.sector} onChange={e => setForm({ ...form, sector: e.target.value })}>
                    <option value="" disabled>{t.selectSector}</option>
                    {sectors.map(s => <option key={s._id} value={s.name}>{s.name}</option>)}
                  </select>
                </div>
                <div><div style={LBL}>{t.lblCategory}</div><select style={INP} value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>{DOWNLOAD_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}</select></div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
                <div><div style={LBL}>{t.lblFileType}</div><select style={INP} value={form.fileType} onChange={e => setForm({ ...form, fileType: e.target.value })}>{["PDF", "ZIP", "DOC", "XLS", "PNG"].map(ft => <option key={ft}>{ft}</option>)}</select></div>
                <div><div style={LBL}>{t.lblFileSize}</div><input style={INP} value={form.fileSize} onChange={e => setForm({ ...form, fileSize: e.target.value })} placeholder="2.4 MB" /></div>
              </div>
              <div>
                <div style={LBL}>{t.lblFile}</div>
                <input
                  type="file"
                  accept=".pdf,.zip,.doc,.docx,.xls,.xlsx,.png"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    const fd = new FormData();
                    fd.append("document", file);
                    try {
                      const res = await api.post("/upload/document", fd, {
                        headers: { "Content-Type": "multipart/form-data" },
                      });
                      const { url, fileSize } = res.data;
                      setForm(f => ({ ...f, fileUrl: url, fileSize }));
                    } catch (err) {
                      alert(err.response?.data?.message || t.errUpload);
                    }
                  }}
                  style={INP}
                />
                {form.fileUrl && (
                  <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                    {t.uploaded} {form.fileUrl.split("/").pop()}
                  </div>
                )}
              </div>
              <div>
                <div style={LBL}>{t.lblThumbnail}</div>
                <ImageUploadField
                  value={form.thumbnailUrl || ""}
                  onChange={url => setForm({ ...form, thumbnailUrl: url })}
                />
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={handleSave} disabled={saving} style={{ flex: 1, padding: 9, borderRadius: 8, background: G, color: "#fff", border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: saving ? 0.7 : 1 }}>
                  {saving ? t.savingBtn : t.saveBtn}
                </button>
                <button onClick={closeModal} style={{ flex: 1, padding: 9, borderRadius: 8, background: "#f3f4f6", color: "#374151", border: "1px solid #e5e7eb", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{t.cancel}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {modal === "delete" && selected && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={closeModal}>
          <div style={{ background: "#fff", borderRadius: 14, width: 380, maxWidth: "95vw", overflow: "hidden" }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: "18px 20px" }}>
              <p style={{ fontSize: 13, color: "#374151", margin: 0 }}>{t.deleteConfirm(selected.title)}</p>
              <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
                <button onClick={handleDelete} disabled={saving} style={{ flex: 1, padding: 9, borderRadius: 8, background: "#ef4444", color: "#fff", border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{t.deleteYes}</button>
                <button onClick={closeModal} style={{ flex: 1, padding: 9, borderRadius: 8, background: "#f3f4f6", color: "#374151", border: "1px solid #e5e7eb", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{t.cancel}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DownloadsAdminPage;
